import api from './client';

// Enums
export enum SaleStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
}

export enum PaymentMethod {
  CASH = 'CASH',
  CREDIT_CARD = 'CREDIT_CARD',
  BANK_TRANSFER = 'BANK_TRANSFER',
  CHECK = 'CHECK',
  MOBILE_PAYMENT = 'MOBILE_PAYMENT',
  OTHER = 'OTHER',
}

export enum SaleItemType {
  BIRD = 'BIRD',
  SUPPLY = 'SUPPLY',
  EQUIPMENT = 'EQUIPMENT',
  SERVICE = 'SERVICE',
  FEED = 'FEED',
  OTHER = 'OTHER',
}

// Interfaces
export interface SaleItem {
  id?: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  itemType?: SaleItemType;
  itemReferenceId?: string;
  notes?: string;
}

export interface Sale {
  id: string;
  invoiceNumber: string;
  saleDate: string;
  buyerName: string;
  buyerPhone: string;
  buyerAddress: string;
  paymentMethod: PaymentMethod;
  total: number;
  status: SaleStatus;
  notes?: string;
  items: SaleItem[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateSaleDto {
  invoiceNumber: string;
  saleDate: Date | string;
  buyerName: string;
  buyerPhone: string;
  buyerAddress: string;
  paymentMethod: PaymentMethod;
  total: number;
  notes?: string;
  items: SaleItem[];
}

export interface UpdateSaleDto extends Partial<CreateSaleDto> {}

export interface GetSalesDto {
  startDate?: string;
  endDate?: string;
  itemType?: SaleItemType;
  status?: SaleStatus;
  customerId?: string;
  paymentMethod?: PaymentMethod;
}

export interface SalesResponse {
  success: boolean;
  data: Sale[];
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

export interface SalesAnalytics {
  totalSalesAmount: number;
  totalSalesCount: number;
  salesAmountByStatus: Record<string, number>;
  salesCountByStatus: Record<string, number>;
}

export interface SalesByStatusAnalytics {
  totalSales: number;
  salesByStatus: Record<string, number>;
  salesCount: number;
}

// API functions
export const salesApi = {
  async getSales(filters: GetSalesDto = {}): Promise<Sale[]> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          params.set(key, value.toString());
        }
      }
    });

    const response = await api.get<Sale[]>(`accounting/sales?${params.toString()}`);
    return response.data;
  },

  async getSale(id: string): Promise<Sale> {
    const response = await api.get<Sale>(`accounting/sales/${id}`);
    return response.data;
  },

  async createSale(data: CreateSaleDto): Promise<Sale> {
    const response = await api.post<Sale>('accounting/sales', data);
    return response.data;
  },

  async updateSale(id: string, data: UpdateSaleDto): Promise<Sale> {
    const response = await api.put<Sale>(`accounting/sales/${id}`, data);
    return response.data;
  },

  async deleteSale(id: string): Promise<void> {
    await api.delete(`accounting/sales/${id}`);
  },

  async getTotalSales(): Promise<{ total: number }> {
    const response = await api.get<{ total: number }>('accounting/sales/total');
    return response.data;
  },

  async getSalesAnalytics(): Promise<SalesAnalytics> {
    const response = await api.get<SalesAnalytics>('accounting/sales/analytics');
    return response.data;
  },

  async getSalesByStatus(): Promise<SalesByStatusAnalytics> {
    const response = await api.get<SalesByStatusAnalytics>('accounting/sales/by-status');
    return response.data;
  },

  async updateSaleStatus(id: string, status: SaleStatus): Promise<Sale> {
    const response = await api.patch<Sale>(`transactions/sales/${id}/status`, { status });
    return response.data;
  },

  async approveSale(id: string): Promise<Sale> {
    const response = await api.patch<Sale>(`transactions/${id}/approve`);
    return response.data;
  },
};

// Helper functions
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount);
};

export const getSaleStatusColor = (status: SaleStatus): string => {
  switch (status) {
    case SaleStatus.PENDING:
      return 'text-yellow-600 bg-yellow-100';
    case SaleStatus.COMPLETED:
      return 'text-green-600 bg-green-100';
    case SaleStatus.CANCELLED:
      return 'text-red-600 bg-red-100';
    case SaleStatus.REFUNDED:
      return 'text-gray-600 bg-gray-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
};

export const getPaymentMethodDisplay = (method: PaymentMethod): string => {
  switch (method) {
    case PaymentMethod.CASH:
      return 'Cash';
    case PaymentMethod.CREDIT_CARD:
      return 'Credit Card';
    case PaymentMethod.BANK_TRANSFER:
      return 'Bank Transfer';
    case PaymentMethod.CHECK:
      return 'Check';
    case PaymentMethod.MOBILE_PAYMENT:
      return 'Mobile Payment';
    case PaymentMethod.OTHER:
      return 'Other';
    default:
      return method;
  }
};

export const getSaleItemTypeDisplay = (itemType: SaleItemType): string => {
  switch (itemType) {
    case SaleItemType.BIRD:
      return 'Bird';
    case SaleItemType.SUPPLY:
      return 'Supply';
    case SaleItemType.EQUIPMENT:
      return 'Equipment';
    case SaleItemType.SERVICE:
      return 'Service';
    case SaleItemType.FEED:
      return 'Feed';
    case SaleItemType.OTHER:
      return 'Other';
    default:
      return itemType || 'Other';
  }
};

export const getSaleItemTypeColor = (itemType: SaleItemType): string => {
  switch (itemType) {
    case SaleItemType.BIRD:
      return 'bg-green-100 text-green-800 border-green-200';
    case SaleItemType.SUPPLY:
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case SaleItemType.EQUIPMENT:
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case SaleItemType.SERVICE:
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case SaleItemType.FEED:
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case SaleItemType.OTHER:
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const calculateSaleTotal = (items: SaleItem[]): number => {
  return items.reduce((total, item) => total + item.totalPrice, 0);
};

export const generateInvoiceNumber = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const time = String(now.getTime()).slice(-4);
  return `INV-${year}${month}${day}-${time}`;
}; 